import { isPlatformBrowser, CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';

type StarSeed = {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  opacity: number;
};

type StarInstance = StarSeed & {
  tileX: number;
  tileY: number;
  instanceId: string;
};

@Component({
  selector: 'app-star-field',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './star-field.html',
  styleUrl: './star-field.scss'
})
export class StarFieldComponent implements OnInit, OnDestroy {
  private readonly maxShift = 80;
  private readonly loopDurationSeconds = 34;
  private readonly maxVelocityChangePerSecond = 0.3;
  private readonly targetFrameIntervalMs = 33;
  private readonly interactionCooldownMs = 300;
  private readonly interactionDeltaScale = 0.18;
  readonly starField = this.createStarField(220);
  private animationFrame: number | null = null;
  private parallaxTarget = { x: 0, y: 0 };
  private parallaxCurrent = { x: 0, y: 0 };
  private lastTimestamp = 0;
  private lastUpdateTime = 0;
  private interactionThrottleUntil = 0;
  private interactionDeltaCarry = 0;
  private pointerDirection = { x: 0, y: 0 };
  private starDirection = { x: 0, y: 0 };
  private targetSpeed = { x: 0, y: 0 };
  private currentSpeed = { x: 0, y: 0 };
  private starOffsetRatio = { x: 0, y: 0 };
  private starVelocity = { x: 0, y: 0 };
  private cssVariableCache = new Map<string, string>();

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.animationFrame = window.requestAnimationFrame((ts) => this.animate(ts));
  }

  ngOnDestroy(): void {
    if (this.animationFrame && this.isBrowser) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  @HostListener('document:mousemove', ['$event'])
  handlePointerMove(event: MouseEvent): void {
    if (!this.isBrowser) {
      return;
    }
    const { innerWidth, innerHeight } = window;
    if (!innerWidth || !innerHeight) {
      return;
    }
    const rawX = ((event.clientX - innerWidth / 2) / (innerWidth / 2)) || 0;
    const rawY = ((event.clientY - innerHeight / 2) / (innerHeight / 2)) || 0;
    const clamp = (value: number) => Math.max(-1, Math.min(1, value));
    const offsetX = clamp(rawX);
    const offsetY = clamp(rawY);
    const magnitudeX = Math.min(1, Math.abs(offsetX));
    const magnitudeY = Math.min(1, Math.abs(offsetY));

    this.pointerDirection = {
      x: magnitudeX > 0.001 ? offsetX / (Math.abs(offsetX) || 1) : 0,
      y: magnitudeY > 0.001 ? offsetY / (Math.abs(offsetY) || 1) : 0
    };

    this.parallaxTarget = {
      x: this.maxShift * offsetX,
      y: this.maxShift * offsetY
    };

    this.targetSpeed = { x: magnitudeX, y: magnitudeY };
  }

  @HostListener('document:mouseleave')
  resetParallax(): void {
    if (!this.isBrowser) {
      return;
    }
    this.parallaxTarget = { x: 0, y: 0 };
    this.targetSpeed = { x: 0, y: 0 };
    this.pointerDirection = { x: 0, y: 0 };
    this.starDirection = { x: 0, y: 0 };
    this.starVelocity = { x: 0, y: 0 };
  }

  @HostListener('window:wheel')
  onWheel(): void {
    if (!this.isBrowser) {
      return;
    }
    const now = performance.now();
    this.interactionThrottleUntil = now + this.interactionCooldownMs;
    this.interactionDeltaCarry = 0;
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (!this.isBrowser) {
      return;
    }
    const now = performance.now();
    this.interactionThrottleUntil = now + this.interactionCooldownMs;
    this.interactionDeltaCarry = 0;
  }

  @HostListener('window:touchmove')
  onTouchMove(): void {
    if (!this.isBrowser) {
      return;
    }
    const now = performance.now();
    this.interactionThrottleUntil = now + this.interactionCooldownMs;
    this.interactionDeltaCarry = 0;
  }

  trackStar(_: number, star: StarInstance): string {
    return star.instanceId;
  }

  private animate(timestamp: number): void {
    if (!this.lastTimestamp) {
      this.lastTimestamp = timestamp;
    }

    const now = performance.now();
    const throttled = now - this.lastUpdateTime < this.targetFrameIntervalMs;
    const duringInteraction = now < this.interactionThrottleUntil;

    let deltaSeconds = (timestamp - this.lastTimestamp) / 1000;
    if (duringInteraction) {
      const scaledDelta = deltaSeconds * this.interactionDeltaScale + this.interactionDeltaCarry;
      this.interactionDeltaCarry = Math.min(scaledDelta, 0.5);
      deltaSeconds = Math.min(scaledDelta, deltaSeconds);
    }

    if (throttled) {
      deltaSeconds = Math.min(deltaSeconds, this.targetFrameIntervalMs / 1000);
    }

    this.lastTimestamp = timestamp;
    this.lastUpdateTime = now;

    const lerpFactor = 0.08;
    this.parallaxCurrent.x += (this.parallaxTarget.x - this.parallaxCurrent.x) * lerpFactor;
    this.parallaxCurrent.y += (this.parallaxTarget.y - this.parallaxCurrent.y) * lerpFactor;

    this.setCssVariable('--parallax-x', `${this.parallaxCurrent.x.toFixed(2)}px`);
    this.setCssVariable('--parallax-y', `${this.parallaxCurrent.y.toFixed(2)}px`);
    const ratioX = this.maxShift ? this.parallaxCurrent.x / this.maxShift : 0;
    const ratioY = this.maxShift ? this.parallaxCurrent.y / this.maxShift : 0;
    this.setCssVariable('--parallax-ratio-x', ratioX.toFixed(4));
    this.setCssVariable('--parallax-ratio-y', ratioY.toFixed(4));

    const easingWindowSeconds = 0.8;
    const accelerationLerp = Math.min(1, deltaSeconds / easingWindowSeconds);
    this.currentSpeed.x += (this.targetSpeed.x - this.currentSpeed.x) * accelerationLerp;
    this.currentSpeed.y += (this.targetSpeed.y - this.currentSpeed.y) * accelerationLerp;

    const directionEaseWindow = 0.6;
    const directionLerp = Math.min(1, deltaSeconds / directionEaseWindow);
    this.starDirection.x += (this.pointerDirection.x - this.starDirection.x) * directionLerp;
    this.starDirection.y += (this.pointerDirection.y - this.starDirection.y) * directionLerp;

    const desiredVelocity = {
      x: this.starDirection.x * this.currentSpeed.x,
      y: this.starDirection.y * this.currentSpeed.y
    };

    const applyVelocityLimit = (current: number, target: number) => {
      const maxDelta = this.maxVelocityChangePerSecond * deltaSeconds;
      if (maxDelta <= 0 || !Number.isFinite(maxDelta)) {
        return target;
      }
      const delta = target - current;
      const clampedDelta = Math.max(-maxDelta, Math.min(maxDelta, delta));
      return current + clampedDelta;
    };

    this.starVelocity.x = applyVelocityLimit(this.starVelocity.x, desiredVelocity.x);
    this.starVelocity.y = applyVelocityLimit(this.starVelocity.y, desiredVelocity.y);

    const travelRatioX = this.loopDurationSeconds
      ? (this.starVelocity.x * deltaSeconds) / this.loopDurationSeconds
      : 0;
    const travelRatioY = this.loopDurationSeconds
      ? (this.starVelocity.y * deltaSeconds) / this.loopDurationSeconds
      : 0;

    this.starOffsetRatio.x = this.wrapRatio(this.starOffsetRatio.x + travelRatioX);
    this.starOffsetRatio.y = this.wrapRatio(this.starOffsetRatio.y + travelRatioY);

    this.setCssVariable('--star-shift-ratio-x', this.starOffsetRatio.x.toFixed(4));
    this.setCssVariable('--star-shift-ratio-y', this.starOffsetRatio.y.toFixed(4));

    this.animationFrame = window.requestAnimationFrame((ts) => this.animate(ts));
  }

  private setCssVariable(name: string, value: string): void {
    const cached = this.cssVariableCache.get(name);
    if (cached === value) {
      return;
    }
    this.cssVariableCache.set(name, value);
    this.host.nativeElement.style.setProperty(name, value);
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private createStarField(count: number): StarInstance[] {
    const seeds = this.generateStars(count);
    const tiles = [-1, 0, 1];
    const instances: StarInstance[] = [];
    for (const seed of seeds) {
      for (const tileX of tiles) {
        for (const tileY of tiles) {
          instances.push({
            ...seed,
            tileX,
            tileY,
            instanceId: `${seed.id}-${tileX}-${tileY}`
          });
        }
      }
    }
    return instances;
  }

  private generateStars(count: number): StarSeed[] {
    const rng = this.createRng(1337);
    const stars: StarSeed[] = [];
    for (let i = 0; i < count; i++) {
      stars.push({
        id: i,
        x: rng(),
        y: rng(),
        size: 1.2 + rng() * 2.8,
        delay: rng() * 5,
        opacity: 0.35 + rng() * 0.65
      });
    }
    return stars;
  }

  private createRng(seed: number): () => number {
    let value = seed >>> 0;
    return () => {
      value = (value * 1664525 + 1013904223) >>> 0;
      return value / 0x100000000;
    };
  }

  private wrapRatio(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }
    const normalized = ((value + 0.5) % 1 + 1) % 1;
    return normalized - 0.5;
  }
}
